import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { retrySalesReport } from '@/lib/services/money/money.query'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import {
  exportAccountingJournalReport,
  exportCashDrawerReport,
  exportClosedDaysReport,
  exportCustomerBalanceReport,
  exportExternalOrdersReport,
  exportFloristPerformanceReport,
  exportPhoneClickReport,
  exportSalesSummaryReport,
  exportSalesTaxReport,
  exportTipsReport,
  getAccountingJournalOrder,
  getAccountingJournalReport,
  getCashDrawerReport,
  getClosedDaysReport,
  getCustomerBalanceReport,
  getExternalOrdersReport,
  getFloristMapReport,
  getFloristPerformanceReport,
  getPhoneClickReport,
  getSalesSummaryReport,
  getSalesTaxReport,
  getTipsReport,
  type ReportExportRequest,
  type ReportQueryKeyEntries,
} from './reports.api'
import type {
  AccountingJournalReportFilters,
  CashDrawerReportFilters,
  ClosedDaysReportFilters,
  CustomerBalanceReportFilters,
  ExternalOrdersReportFilters,
  FloristPerformanceReportFilters,
  PhoneClickReportFilters,
  SalesSummaryReportFilters,
  SalesTaxReportFilters,
  TipsReportFilters,
} from './reports.schema'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface ReportScope<TFilters> extends OrganizationScope {
  filters?: TFilters | undefined
}

/** The key entries each report fetcher reads; the scoped key builders below set them. */
type ReportQueryKey<TFilters> = [AdminQueryRoot & ReportQueryKeyEntries<TFilters>]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type ReportQueryParams<TOptions> = OrganizationScope &
  Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

function reportQueryKey<TFilters>(
  scope: string,
  { organizationId, filters }: ReportScope<TFilters>
): ReportQueryKey<TFilters> {
  return buildScopedAdminQueryKey(reportQueryKeys.all({ organizationId })[0], {
    organizationId,
    scope,
    filters,
  })
}

export const reportQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'report' }, { organizationId }),
  ],
  phoneClicks: (scope: ReportScope<PhoneClickReportFilters> = {}) =>
    reportQueryKey('phoneClicks', scope),
  salesSummary: (scope: ReportScope<SalesSummaryReportFilters> = {}) =>
    reportQueryKey('salesSummary', scope),
  customerBalances: (scope: ReportScope<CustomerBalanceReportFilters> = {}) =>
    reportQueryKey('customerBalances', scope),
  closedDays: (scope: ReportScope<ClosedDaysReportFilters> = {}) =>
    reportQueryKey('closedDays', scope),
  accountingJournal: (scope: ReportScope<AccountingJournalReportFilters> = {}) =>
    reportQueryKey('accountingJournal', scope),
  accountingJournalOrder: (scope: ReportScope<{ salesOrderId: number }> = {}) =>
    reportQueryKey('accountingJournalOrder', scope),
  salesTax: (scope: ReportScope<SalesTaxReportFilters> = {}) => reportQueryKey('salesTax', scope),
  tips: (scope: ReportScope<TipsReportFilters> = {}) => reportQueryKey('tips', scope),
  externalOrders: (scope: ReportScope<ExternalOrdersReportFilters> = {}) =>
    reportQueryKey('externalOrders', scope),
  cashDrawer: (scope: ReportScope<CashDrawerReportFilters> = {}) =>
    reportQueryKey('cashDrawer', scope),
  floristPerformance: (scope: ReportScope<FloristPerformanceReportFilters> = {}) =>
    reportQueryKey('floristPerformance', scope),
  floristMap: (scope: OrganizationScope = {}) => reportQueryKey<undefined>('floristMap', scope),
}

export const reportQueries = {
  floristMap: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: reportQueryKeys.floristMap({ organizationId }),
      queryFn: getFloristMapReport,
    }),
  phoneClicks: ({ organizationId, filters }: ReportScope<PhoneClickReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.phoneClicks({ organizationId, filters }),
      queryFn: getPhoneClickReport,
    }),
  salesSummary: ({ organizationId, filters }: ReportScope<SalesSummaryReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.salesSummary({ organizationId, filters }),
      queryFn: getSalesSummaryReport,
    }),
  customerBalances: ({ organizationId, filters }: ReportScope<CustomerBalanceReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.customerBalances({ organizationId, filters }),
      queryFn: getCustomerBalanceReport,
    }),
  accountingJournal: ({
    organizationId,
    filters,
  }: ReportScope<AccountingJournalReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.accountingJournal({ organizationId, filters }),
      queryFn: getAccountingJournalReport,
      // The page shows an incomplete or failed journal inline, like Sales Summary.
      throwOnError: false,
      retry: retrySalesReport,
    }),
  accountingJournalOrder: ({
    organizationId,
    filters,
  }: ReportScope<{ salesOrderId: number }> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.accountingJournalOrder({ organizationId, filters }),
      queryFn: getAccountingJournalOrder,
      throwOnError: false,
      retry: retrySalesReport,
    }),
  closedDays: ({ organizationId, filters }: ReportScope<ClosedDaysReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.closedDays({ organizationId, filters }),
      queryFn: getClosedDaysReport,
    }),
  salesTax: ({ organizationId, filters }: ReportScope<SalesTaxReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.salesTax({ organizationId, filters }),
      queryFn: getSalesTaxReport,
      // The page shows an incomplete or failed report inline, like Sales Summary.
      throwOnError: false,
      retry: retrySalesReport,
    }),
  tips: ({ organizationId, filters }: ReportScope<TipsReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.tips({ organizationId, filters }),
      queryFn: getTipsReport,
    }),
  externalOrders: ({ organizationId, filters }: ReportScope<ExternalOrdersReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.externalOrders({ organizationId, filters }),
      queryFn: getExternalOrdersReport,
    }),
  cashDrawer: ({ organizationId, filters }: ReportScope<CashDrawerReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.cashDrawer({ organizationId, filters }),
      queryFn: getCashDrawerReport,
    }),
  floristPerformance: ({
    organizationId,
    filters,
  }: ReportScope<FloristPerformanceReportFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: reportQueryKeys.floristPerformance({ organizationId, filters }),
      queryFn: getFloristPerformanceReport,
      placeholderData: (previousData, previousQuery) => {
        const previousFilters = previousQuery?.queryKey[0].filters
        const hasSameShape =
          previousFilters?.view === filters?.view &&
          (filters?.view !== 'momentum' ||
            previousFilters?.momentum_metric === filters?.momentum_metric)

        return hasSameShape ? previousData : undefined
      },
    }),
}

export function usePhoneClickReport(
  filters?: PhoneClickReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.phoneClicks>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.phoneClicks({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportPhoneClickReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<PhoneClickReportFilters>) =>
      exportPhoneClickReport({ organizationId, filters }),
  })
}

export function useSalesSummaryReport(
  filters?: SalesSummaryReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.salesSummary>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.salesSummary({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportSalesSummaryReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<SalesSummaryReportFilters>) =>
      exportSalesSummaryReport({ organizationId, filters }),
  })
}

export function useCustomerBalanceReport(
  filters?: CustomerBalanceReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.customerBalances>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.customerBalances({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportCustomerBalanceReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<CustomerBalanceReportFilters>) =>
      exportCustomerBalanceReport({ organizationId, filters }),
  })
}

export function useAccountingJournalReport(
  filters?: AccountingJournalReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.accountingJournal>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.accountingJournal({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

/** Everything one order has posted, for the order's journal page. */
export function useAccountingJournalOrder(salesOrderId: number) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    ...reportQueries.accountingJournalOrder({ organizationId, filters: { salesOrderId } }),
    // The page's loader already read it; a failed journal shows inline rather than re-reading all history on mount.
    retryOnMount: false,
  })
}

export function useExportAccountingJournalReportMutation() {
  return useMutation({
    mutationFn: ({
      organizationId,
      filters,
    }: ReportExportRequest<AccountingJournalReportFilters>) =>
      exportAccountingJournalReport({ organizationId, filters }),
  })
}

export function useClosedDaysReport(
  filters?: ClosedDaysReportFilters,
  { organizationId, ...params }: ReportQueryParams<ReturnType<typeof reportQueries.closedDays>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.closedDays({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportClosedDaysReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<ClosedDaysReportFilters>) =>
      exportClosedDaysReport({ organizationId, filters }),
  })
}

export function useSalesTaxReport(
  filters?: SalesTaxReportFilters,
  { organizationId, ...params }: ReportQueryParams<ReturnType<typeof reportQueries.salesTax>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.salesTax({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportSalesTaxReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<SalesTaxReportFilters>) =>
      exportSalesTaxReport({ organizationId, filters }),
  })
}

export function useTipsReport(
  filters?: TipsReportFilters,
  { organizationId, ...params }: ReportQueryParams<ReturnType<typeof reportQueries.tips>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.tips({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportTipsReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<TipsReportFilters>) =>
      exportTipsReport({ organizationId, filters }),
  })
}

export function useExternalOrdersReport(
  filters?: ExternalOrdersReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.externalOrders>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.externalOrders({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportExternalOrdersReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<ExternalOrdersReportFilters>) =>
      exportExternalOrdersReport({ organizationId, filters }),
  })
}

export function useCashDrawerReport(
  filters?: CashDrawerReportFilters,
  { organizationId, ...params }: ReportQueryParams<ReturnType<typeof reportQueries.cashDrawer>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.cashDrawer({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportCashDrawerReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<CashDrawerReportFilters>) =>
      exportCashDrawerReport({ organizationId, filters }),
  })
}

export function useFloristPerformanceReport(
  filters?: FloristPerformanceReportFilters,
  {
    organizationId,
    ...params
  }: ReportQueryParams<ReturnType<typeof reportQueries.floristPerformance>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...reportQueries.floristPerformance({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportFloristPerformanceReportMutation() {
  return useMutation({
    mutationFn: ({
      organizationId,
      filters,
    }: ReportExportRequest<FloristPerformanceReportFilters>) =>
      exportFloristPerformanceReport({ organizationId, filters }),
  })
}
